/**
 * Het formulier voor een nieuwe les: welke tekst, welke groep, en de
 * instellingen voor de hele groep.
 *
 * Kies je een groep, dan komt de illustratiestijl van die groep alvast in
 * het formulier (die volgt uit het leerjaar). Een niveau kies je hier niet:
 * elke leerling leest op zijn eigen niveau, of op een afwijking die je per
 * leerling instelt (services/lesinstellingen.py). Er stond een niveau voor
 * de les; dat gold bijna nooit en is weg.
 *
 * Er stond ook "Chat met AI"; die instelling deed niets en is weg. De lengte
 * van de tekst kies je ook niet meer: die hoort bij het niveau (stap 18), en
 * de tekst op elk niveau keurde de beheerder vooraf goed; in de lijst staan
 * alleen teksten die daarmee klaar zijn. Aanmaken duurt even: de server
 * maakt dan meteen de toets op elk niveau dat in de groep voorkomt.
 *
 * Een tekst in deelteksten (een verhaal of een dakpanset) kan ook in een
 * groepsles (stap 23): elk kind leest één deel, in een groepje van vier dat
 * de app voorstelt; er is dan geen toets. Een dakpanset kan alleen zo. Bij
 * een verhaal (vijf delen) vertelt de leerkracht er één zelf: standaard het
 * deel dat de opzet noemt.
 */
import React from 'react';
import { Save } from 'lucide-react';
import { Draaier } from '../../../components/Laden';
import {
  AANTALLEN_MEERKEUZE, AANTALLEN_OPEN, ILLUSTRATIESTIJLEN, heeftDeelteksten, isDakpan, isVerhaal,
} from '../../../keuzelijsten';

export const NIEUWE_LES = {
  text_id: '',
  class_id: '',
  naam: '',
  close_reading_aantal: 5,
  evaluatie_aantal: 2,
  illustratie_stijl: 'schattig',
  vorm: '',            // 'dakpan': een groepsles
  verteld_deel: '',    // bij een verhaal in een groepsles; leeg: zoals de opzet voorstelt
};

/** Is dit een groepsles? Een dakpanset altijd; een verhaal als de leerkracht het koos. */
export const isGroepsles = (les, tekst) => isDakpan(tekst) || (heeftDeelteksten(tekst) && les.vorm === 'dakpan');

/** De les zoals de server hem krijgt: de vorm alleen bij een tekst in deelteksten, het vertelde deel alleen bij een verhaal. */
export function lesVoorServer(les, tekst) {
  const groep = isGroepsles(les, tekst);
  return {
    ...les,
    vorm: groep ? 'dakpan' : null,
    verteld_deel: groep && isVerhaal(tekst) && les.verteld_deel ? parseInt(les.verteld_deel) : null,
  };
}

export function LesFormulier({ les, wijzig, teksten, groepen, onKiesGroep, bezig, onAanmaken, onAnnuleer }) {
  const zet = (veld, omzetten = (w) => w) => (e) => wijzig({ ...les, [veld]: omzetten(e.target.value) });
  const getal = (w) => parseInt(w);
  const tekst = teksten.find(t => t.id === les.text_id);
  const groepsles = isGroepsles(les, tekst);

  const verstuur = (e) => {
    e.preventDefault();
    onAanmaken();
  };

  return (
    <form onSubmit={verstuur} className="card mb-16" style={{ background: 'var(--vlak)' }}>
      <h3 className="mb-16">Nieuwe les aanmaken</h3>
      <div className="grid-3">
        <div className="form-group">
          <label>Tekst</label>
          <select className="input" value={les.text_id} onChange={zet('text_id')} required>
            <option value="">Selecteer tekst</option>
            {teksten.map(t => <option key={t.id} value={t.id}>{t.titel}{isDakpan(t) ? ' (dakpanset)' : ''}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Groep</label>
          <select className="input" value={les.class_id} onChange={e => onKiesGroep(e.target.value)} required>
            <option value="">Selecteer groep</option>
            {groepen.map(g => <option key={g.id} value={g.id}>{g.naam}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Lesnaam (optioneel)</label>
          <input type="text" className="input" value={les.naam} onChange={zet('naam')} />
        </div>
      </div>

      {heeftDeelteksten(tekst) && (
        <div className="card mt-8" style={{ background: 'var(--wit)', padding: '12px 16px' }} data-testid="lesvorm">
          <strong>Hoe lezen de kinderen deze tekst?</strong>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, opacity: isDakpan(tekst) ? 0.5 : 1 }}>
            <input type="radio" name="vorm" checked={!groepsles} disabled={isDakpan(tekst)}
              onChange={() => wijzig({ ...les, vorm: '' })} />
            Iedereen de hele tekst, met een toets
            {isDakpan(tekst) && <span className="text-muted text-small"> (niet bij een dakpanset: door de overlap is het geen tekst om in zijn geheel te lezen)</span>}
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
            <input type="radio" name="vorm" checked={groepsles} onChange={() => wijzig({ ...les, vorm: 'dakpan' })} />
            Groepsles: elk kind leest één deel, op zijn eigen niveau, en het groepje maakt er samen één verhaal van
          </label>
          {groepsles && (
            <p className="text-muted text-small mt-8">
              De app stelt groepjes van vier voor, met verschillende niveaus; blijven er kinderen over, dan lezen er in
              een groepje van vijf twee hetzelfde deel. U kunt de groepjes daarna aanpassen. Er is geen toets: het
              gesprek in het groepje en de reconstructie zijn op papier.
            </p>
          )}
          {groepsles && isVerhaal(tekst) && (
            <div className="form-group mt-8" style={{ maxWidth: 360 }}>
              <label htmlFor="verteld-deel">Het deel dat u zelf vertelt (een verhaal heeft vijf delen)</label>
              <select id="verteld-deel" className="input" value={les.verteld_deel || ''} onChange={zet('verteld_deel')}>
                <option value="">Zoals de opzet voorstelt</option>
                {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>Deel {n}</option>)}
              </select>
            </div>
          )}
        </div>
      )}

      <h4 className="mt-16 mb-8">Groepsinstellingen</h4>
      <div className="grid-3">
        <div className="form-group">
          <label>Illustratie stijl</label>
          <select className="input" value={les.illustratie_stijl} onChange={zet('illustratie_stijl')}>
            {ILLUSTRATIESTIJLEN.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Tekstlengte</label>
          <p className="text-muted" style={{ fontSize: 13, marginTop: 6 }}>Volgt het niveau van elke leerling</p>
        </div>
      </div>
      {!groepsles && <div className="grid-3 mt-8">
        <div className="form-group">
          <label>Close Reading (MC)</label>
          <select className="input" value={les.close_reading_aantal} onChange={zet('close_reading_aantal', getal)}>
            {AANTALLEN_MEERKEUZE.map(n => <option key={n} value={n}>{n} vragen</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Evaluatie (open)</label>
          <select className="input" value={les.evaluatie_aantal} onChange={zet('evaluatie_aantal', getal)}>
            {AANTALLEN_OPEN.map(n => <option key={n} value={n}>{n} vragen</option>)}
          </select>
        </div>
      </div>}

      <div className="flex gap-8 mt-16">
        <button type="submit" className="btn btn-success" disabled={bezig}>
          {bezig ? <><Draaier /> {groepsles ? 'Groepjes maken...' : 'Toets genereren...'}</> : <><Save size={16} /> Aanmaken</>}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onAnnuleer} disabled={bezig}>Annuleren</button>
      </div>
      {bezig && !groepsles && (
        <p className="text-muted mt-8" style={{ fontSize: '13px' }}>De toets wordt nu gemaakt, voor elk niveau in de groep. Dit kan even duren...</p>
      )}
    </form>
  );
}
